import {
  groupNameMap,
  groupOrder,
  vhcCheckBrakeItemNames,
  vhcCheckItemNames,
  vhcCheckWheelTyreItemNames
} from './constants';
import { ConditionStatus } from './types';
import {
  VHCRecordCheckBrakeItemTypes,
  VHCRecordCheckItemTypes,
  VHCRecordCheckWheelTyreItemTypes,
  VHCRecordItemGroups
} from 'api/generated';
import {
  RecordCheckBrakeItem,
  RecordCheckItem,
  RecordCheckWheelTyreItem,
  VhcItem,
  VhcItemFormType
} from 'features/vhc/model';

type GroupedItems = Record<VHCRecordItemGroups, VhcItem[]>;

export function getSortedGroups(items: VhcItem[]) {
  if (!items) return null;

  const groupedItems = items.reduce((acc, item) => {
    const group = item.group;
    if (!acc[group!]) {
      acc[group!] = [];
    }
    acc[group!].push(item);
    return acc;
  }, {} as GroupedItems);

  return groupOrder
    .filter((group) => groupedItems[group]?.length > 0)
    .map((group) => ({
      group,
      groupName: groupNameMap[group],
      items: groupedItems[group]
    }));
}

export const createVhcItem = (
  id: number,
  type: VHCRecordCheckItemTypes | VHCRecordCheckBrakeItemTypes | VHCRecordCheckWheelTyreItemTypes,
  group: VHCRecordItemGroups,
  notes = '',
  formType: VhcItemFormType = 'default'
): VhcItem => {
  // Базовые поля, которые нужны для всех типов элементов
  const baseItem = {
    id,
    type,
    group,
    conditionId: ConditionStatus.NOT_TESTED,
    notes,
    imageUrls: [],
    labours: [],
    parts: [],
    formType
  };

  // В зависимости от группы возвращаем разные типы DTO
  if (group === VHCRecordItemGroups.BrakesAndHubs) {
    return {
      ...baseItem,
      description: vhcCheckBrakeItemNames[type],
      formType: 'brake',
      isNew: true
    } as unknown as RecordCheckBrakeItem & VhcItemFormType;
  } else if (group === VHCRecordItemGroups.WheelsAndTyres) {
    return {
      ...baseItem,
      description: vhcCheckWheelTyreItemNames[type],
      formType: 'wheelTyre',
      isNew: true,
      make: null,
      model: null,
      size: null,
      dotCode: null,
      readingInner: null,
      readingMiddle: null,
      readingOuter: null
    } as unknown as RecordCheckWheelTyreItem & VhcItemFormType;
  } else {
    return {
      ...baseItem,
      description: vhcCheckItemNames[type],
      formType: 'default',
      isNew: true
    } as unknown as RecordCheckItem;
  }
};

export const defaultVhcItems: VhcItem[] = [
  // External / Drive In
  createVhcItem(1, VHCRecordCheckItemTypes.Engine, VHCRecordItemGroups.ExternalOrDriveIn),
  createVhcItem(2, VHCRecordCheckItemTypes.Transmission, VHCRecordItemGroups.ExternalOrDriveIn),
  createVhcItem(3, VHCRecordCheckItemTypes.Horn, VHCRecordItemGroups.ExternalOrDriveIn),
  createVhcItem(4, VHCRecordCheckItemTypes.ExteriorLights, VHCRecordItemGroups.ExternalOrDriveIn),
  createVhcItem(5, VHCRecordCheckItemTypes.WiperBladesAndArms, VHCRecordItemGroups.ExternalOrDriveIn),
  createVhcItem(6, VHCRecordCheckItemTypes.Misc, VHCRecordItemGroups.ExternalOrDriveIn),

  // Internal
  createVhcItem(7, VHCRecordCheckItemTypes.Interior, VHCRecordItemGroups.Internal),
  createVhcItem(9, VHCRecordCheckItemTypes.InteriorLampsAndDashLights, VHCRecordItemGroups.Internal),
  createVhcItem(10, VHCRecordCheckItemTypes.WashersAndWipersFunction, VHCRecordItemGroups.Internal),
  createVhcItem(11, VHCRecordCheckItemTypes.Misc, VHCRecordItemGroups.Internal),

  // Under Bonnet
  createVhcItem(12, VHCRecordCheckItemTypes.Belts, VHCRecordItemGroups.UnderBonnet),
  createVhcItem(13, VHCRecordCheckItemTypes.EngineOil, VHCRecordItemGroups.UnderBonnet),
  createVhcItem(14, VHCRecordCheckItemTypes.Coolant, VHCRecordItemGroups.UnderBonnet),
  createVhcItem(15, VHCRecordCheckItemTypes.Screenwash, VHCRecordItemGroups.UnderBonnet),
  createVhcItem(16, VHCRecordCheckItemTypes.BrakeFluid, VHCRecordItemGroups.UnderBonnet),
  createVhcItem(17, VHCRecordCheckItemTypes.Misc, VHCRecordItemGroups.UnderBonnet),

  // Underside
  createVhcItem(18, VHCRecordCheckItemTypes.BrakeDiscsAndPadsFOrR, VHCRecordItemGroups.Underside),
  createVhcItem(19, VHCRecordCheckItemTypes.Exhaust, VHCRecordItemGroups.Underside),
  createVhcItem(20, VHCRecordCheckItemTypes.Steering, VHCRecordItemGroups.Underside),
  createVhcItem(21, VHCRecordCheckItemTypes.Suspension, VHCRecordItemGroups.Underside),
  createVhcItem(22, VHCRecordCheckItemTypes.Misc, VHCRecordItemGroups.Underside),

  // Brakes & Hubs
  createVhcItem(23, VHCRecordCheckBrakeItemTypes.FrontPadsAndDiscs, VHCRecordItemGroups.BrakesAndHubs),
  createVhcItem(24, VHCRecordCheckBrakeItemTypes.FrontBrakesOther, VHCRecordItemGroups.BrakesAndHubs),
  createVhcItem(25, VHCRecordCheckBrakeItemTypes.RearPadsAndDiscs, VHCRecordItemGroups.BrakesAndHubs),
  createVhcItem(26, VHCRecordCheckBrakeItemTypes.RearBrakesOther, VHCRecordItemGroups.BrakesAndHubs),
  createVhcItem(27, VHCRecordCheckBrakeItemTypes.Misc, VHCRecordItemGroups.BrakesAndHubs),

  // Wheels & Tyres
  createVhcItem(28, VHCRecordCheckWheelTyreItemTypes.FronLeftTyre, VHCRecordItemGroups.WheelsAndTyres),
  createVhcItem(29, VHCRecordCheckWheelTyreItemTypes.FronRightTyre, VHCRecordItemGroups.WheelsAndTyres),
  createVhcItem(30, VHCRecordCheckWheelTyreItemTypes.RearLeftTyre, VHCRecordItemGroups.WheelsAndTyres),
  createVhcItem(31, VHCRecordCheckWheelTyreItemTypes.RearRightTyre, VHCRecordItemGroups.WheelsAndTyres),
  createVhcItem(32, VHCRecordCheckWheelTyreItemTypes.SpareTyre, VHCRecordItemGroups.WheelsAndTyres),
  createVhcItem(33, VHCRecordCheckWheelTyreItemTypes.InflationKitOrSpaceSever, VHCRecordItemGroups.WheelsAndTyres),
  createVhcItem(34, VHCRecordCheckWheelTyreItemTypes.Misc, VHCRecordItemGroups.WheelsAndTyres)
];
